import Skeleton from './Skeleton';

// Ready-made loading layouts built from the shimmering Skeleton block. Each mirrors the real thing it stands in for
// (same row heights and spacing) so content doesn't jump when it arrives. Widths are fixed, not random, so a skeleton
// looks the same every time and doesn't flicker on re-render.

const NAME_WIDTHS = ['w-24', 'w-32', 'w-20', 'w-28'];
const LINE_WIDTHS = ['w-3/4', 'w-1/2', 'w-2/3', 'w-5/6', 'w-2/5'];

/** A channel's message history: avatar, name and one or two lines of text per message. */
export function MessageListSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div aria-hidden className="space-y-5 px-2 py-4" role="presentation">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-start gap-3">
          <Skeleton className="h-9 w-9 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1 space-y-2 pt-0.5">
            <Skeleton className={`h-3 ${NAME_WIDTHS[i % NAME_WIDTHS.length]}`} />
            <Skeleton className={`h-3.5 ${LINE_WIDTHS[i % LINE_WIDTHS.length]}`} />
            {i % 3 !== 1 && <Skeleton className={`h-3.5 ${LINE_WIDTHS[(i + 2) % LINE_WIDTHS.length]}`} />}
          </div>
        </div>
      ))}
    </div>
  );
}

/** Rows of the channel column: an icon and a name, at the real row height. */
export function ChannelListSkeleton({ rows = 7 }: { rows?: number }) {
  return (
    <div aria-hidden className="space-y-1 p-1" role="presentation">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex min-h-8 items-center gap-2.5 px-2">
          <Skeleton className="h-4 w-4 shrink-0 rounded" />
          <Skeleton className={`h-3 ${NAME_WIDTHS[(i + 1) % NAME_WIDTHS.length]}`} />
        </div>
      ))}
    </div>
  );
}

/** Rows of the People list: an avatar and a name. */
export function PeopleListSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div aria-hidden className="space-y-1 p-1" role="presentation">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-2.5 px-2 py-1.5">
          <Skeleton className="h-7 w-7 shrink-0 rounded-full" />
          <Skeleton className={`h-3 ${NAME_WIDTHS[i % NAME_WIDTHS.length]}`} />
        </div>
      ))}
    </div>
  );
}

/** A Kanban board: a few columns, each with a title and some cards. */
export function KanbanBoardSkeleton({ columns = 3 }: { columns?: number }) {
  return (
    <div aria-hidden className="flex gap-3 overflow-hidden pb-2" role="presentation">
      {Array.from({ length: columns }, (_, c) => (
        <div key={c} className="surface-glass w-[240px] shrink-0 rounded-xl p-2">
          <div className="mb-3 space-y-2 py-1">
            <Skeleton className="mx-auto h-4 w-24" />
            <Skeleton className="mx-auto h-3 w-32" />
          </div>
          <Skeleton className="mb-2 h-8 w-full rounded-lg" />
          <div className="space-y-2">
            {Array.from({ length: 3 - (c % 2) }, (_, i) => (
              <Skeleton key={i} className="h-16 w-full rounded-lg" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/** The whole app while the session is being checked: channel column (wide screens), a header, messages and a composer. */
export function AppShellSkeleton() {
  return (
    <div aria-busy="true" aria-label="Loading" role="status" className="h-app min-h-screen w-full brand-app-bg flex overflow-hidden">
      <aside className="hidden md:flex w-64 shrink-0 flex-col gap-3 border-r border-white/5 p-3">
        <Skeleton className="h-8 w-32" />
        <ChannelListSkeleton rows={9} />
      </aside>
      <main className="flex min-w-0 flex-1 flex-col">
        <div className="flex h-12 shrink-0 items-center gap-3 border-b border-white/5 px-4">
          <Skeleton className="h-4 w-4 rounded" />
          <Skeleton className="h-4 w-28" />
        </div>
        <div className="min-h-0 flex-1 overflow-hidden px-2">
          <MessageListSkeleton rows={8} />
        </div>
        <div className="shrink-0 p-3">
          <Skeleton className="h-11 w-full rounded-xl" />
        </div>
      </main>
    </div>
  );
}
